import classNames from 'classnames'
import { forwardRef, type ReactNode, InputHTMLAttributes, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react'
import { Label } from './Label'
import styles from './Field.module.css'

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  info?: ReactNode
  infoTitle?: string
}

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string
  error?: string
  info?: ReactNode
  infoTitle?: string
}

interface SelectOption {
  value: string
  label: string
}

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string
  error?: string
  info?: ReactNode
  infoTitle?: string
  options: SelectOption[]
  placeholder?: string
}

const FieldWrapper = ({ children }: { children: ReactNode }) => (
  <div className={styles['field']}>{children}</div>
)

const FieldError = ({ children }: { children: ReactNode }) => (
  <p className={styles['error']}>{children}</p>
)

const FieldInput = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, info, infoTitle, id, className = '', ...props }, ref) => {
    const inputId = id ?? label?.toLowerCase().replace(/\s+/g, '-')
    return (
      <FieldWrapper>
        {label && (
          <Label htmlFor={inputId} info={info} infoTitle={infoTitle}>
            {label}
          </Label>
        )}
        <input
          ref={ref}
          id={inputId}
          className={classNames(styles['control'], error && styles['invalid'], className)}
          {...props}
        />
        {error && <FieldError>{error}</FieldError>}
      </FieldWrapper>
    )
  },
)
FieldInput.displayName = 'Field.Input'

const FieldTextarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, info, infoTitle, id, className = '', ...props }, ref) => {
    const inputId = id ?? label?.toLowerCase().replace(/\s+/g, '-')
    return (
      <FieldWrapper>
        {label && (
          <Label htmlFor={inputId} info={info} infoTitle={infoTitle}>
            {label}
          </Label>
        )}
        <textarea
          ref={ref}
          id={inputId}
          rows={4}
          className={classNames(styles['control'], styles['textarea'], error && styles['invalid'], className)}
          {...props}
        />
        {error && <FieldError>{error}</FieldError>}
      </FieldWrapper>
    )
  },
)
FieldTextarea.displayName = 'Field.Textarea'

const FieldSelect = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, error, info, infoTitle, options, placeholder, id, className = '', ...props }, ref) => {
    const inputId = id ?? label?.toLowerCase().replace(/\s+/g, '-')
    return (
      <FieldWrapper>
        {label && (
          <Label htmlFor={inputId} info={info} infoTitle={infoTitle}>
            {label}
          </Label>
        )}
        <select
          ref={ref}
          id={inputId}
          className={classNames(styles['control'], error && styles['invalid'], className)}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {error && <FieldError>{error}</FieldError>}
      </FieldWrapper>
    )
  },
)
FieldSelect.displayName = 'Field.Select'

export const Field = Object.assign(FieldWrapper, {
  Input: FieldInput,
  Textarea: FieldTextarea,
  Select: FieldSelect,
})
